import { InputRule, inputRules } from 'prosemirror-inputrules';
import { Plugin } from 'prosemirror-state';
import { SENTENCE_END_SOURCE } from 'dashboard/helper/editorHelper';

const LEAF_SENTINEL = '￼';
// Same window prosemirror-inputrules reads back from the caret.
const MAX_MATCH = 500;
const SENTENCE_START_LOWERCASE = new RegExp(
  String.raw`(?:^|${LEAF_SENTINEL}|${SENTENCE_END_SOURCE})(\p{Ll})$`,
  'u'
);
const DOC_START = 1;

// Fallback for callers that don't know about signatures: whatever the rule is
// about to leave behind must be blank. Editor.vue passes a signature-aware
// predicate instead, since a new draft may already hold the agent's signature.
const isRemainderBlank = (state, from) =>
  state.doc.textBetween(from, state.doc.content.size, '\n', '\n').trim() === '';

function endOfLine(state, from) {
  const $from = state.doc.resolve(from);
  const blockStart = $from.start();
  let breakPos = null;
  $from.parent.forEach((node, offset) => {
    const pos = blockStart + offset;
    if (breakPos === null && pos >= from && node.type.name === 'hard_break') {
      breakPos = pos;
    }
  });
  return breakPos === null ? $from.end() : breakPos;
}

const isRestOfLineBlank = (state, from) =>
  state.doc.textBetween(from, endOfLine(state, from), '\n', '\n').trim() === '';

function followsLineBreak(state, letterStart) {
  return state.doc.resolve(letterStart).nodeBefore?.type.name === 'hard_break';
}

export function capitalizeFirstLetterInputRule(
  isDraftEmpty = isRemainderBlank
) {
  return new InputRule(SENTENCE_START_LOWERCASE, (state, match, start, end) => {
    const letter = match[1];
    // `start` points at the whole match, which includes whatever marked the
    // sentence boundary. Replacing from there would swallow the line break or
    // the punctuation that triggered the rule.
    const boundary = match[0].slice(0, match[0].length - letter.length);
    const letterStart = start + boundary.length;

    const afterLineBreak = boundary.startsWith(LEAF_SENTINEL);
    if (afterLineBreak && !followsLineBreak(state, letterStart)) {
      return null;
    }

    // Terminal punctuation is unambiguous: whatever follows it starts a new
    // sentence, even mid-paragraph and even when the agent is inserting into
    // text they already wrote. The start of a block or of a line is a weaker
    // signal — it is equally true when the caret was moved back to the start of
    // finished text, which is an edit. There the rule needs the rest of the
    // line to be blank. The signature escape hatch applies only at the very
    // start of the document: that is the one place a non-blank remainder still
    // means "brand new draft", because ReplyBox pre-filled it with the
    // signature. Anywhere else a signature-only remainder just means the caret
    // sits inside the signature itself, which must be left alone.
    const afterSentenceEnd = boundary !== '' && !afterLineBreak;
    const startsDraftWithSignature =
      letterStart === DOC_START && isDraftEmpty(state, end);
    if (
      !afterSentenceEnd &&
      !isRestOfLineBlank(state, end) &&
      !startsDraftWithSignature
    ) {
      return null;
    }
    return state.tr.insertText(letter.toUpperCase(), letterStart, end);
  });
}

// Input rules never see composed text: prosemirror-inputrules bails while
// `view.composing` is set, and the composed character arrives through a DOM
// mutation rather than handleTextInput. That covers every dead-key accent on an
// ABNT2 keyboard (' then a => á), which is exactly how a Brazilian agent starts
// words like "Ótimo" or "Última". Re-running the same rule once the composition
// lands is what keeps those sentences capitalized.
export function capitalizeComposedLetter(view, rule, composed) {
  const { state } = view;
  if (!state.selection.empty) return false;

  const caret = state.selection.from;
  const $caret = state.doc.resolve(caret);
  const textBefore = $caret.parent.textBetween(
    Math.max(0, $caret.parentOffset - MAX_MATCH),
    $caret.parentOffset,
    null,
    LEAF_SENTINEL
  );
  const match = rule.match.exec(textBefore);
  if (!match) return false;
  // Only the character the composition just produced may be rewritten. Without
  // this the rule would uppercase whatever letter happens to sit at the caret —
  // text the agent typed earlier, or pasted in.
  if (match[1] !== composed) return false;

  // The composed character is already in the document, so the match ends at the
  // caret instead of one character short of it the way a typed match does.
  const tr = rule.handler(state, match, caret - match[0].length, caret);
  if (!tr) return false;
  view.dispatch(tr);
  return true;
}

function compositionPlugin(rule) {
  // ProseMirror folds the composition into the document on its own schedule:
  // compositionend runs before the selection catches up, and forceFlush is a
  // no-op unless a flush is already pending. Reading on the next view update
  // avoids any timing assumption. The pending text is dropped after that single
  // update — leaving it armed would let a later caret move (a mid-word accent
  // produces no match, so nothing consumes the flag) rewrite a letter the agent
  // never composed.
  const pending = new WeakMap();
  return new Plugin({
    props: {
      handleDOMEvents: {
        compositionend: (view, event) => {
          pending.set(view, event.data);
          return false;
        },
      },
    },
    view: () => ({
      update(view) {
        if (!pending.has(view)) return;
        const composed = pending.get(view);
        pending.delete(view);
        if (composed) capitalizeComposedLetter(view, rule, composed);
      },
    }),
  });
}

export function capitalizeFirstLetterPlugins(isDraftEmpty) {
  const rule = capitalizeFirstLetterInputRule(isDraftEmpty);
  return [inputRules({ rules: [rule] }), compositionPlugin(rule)];
}
